import { toast, type Id as ToastId } from 'react-toastify';
import { translate } from '../utils/translate';

export type MatchStreamEventName =
  | 'heartbeat'
  | 'server.stopping'
  | 'connected'
  | 'match.updated'
  | 'match.deleted'
  | 'live_event.created'
  | 'live_event.updated'
  | 'live_event.deleted'
  | 'live_events.reset'
  | 'roadmap.updated'
  | 'presence.updated';

export type PresenceUpdatedPayload = {
  channelKey?: string;
  matchId?: string;
  matchKey?: string;
  liveUsers?: number;
};

export type MatchStreamPayload =
  | PresenceUpdatedPayload
  | Record<string, unknown>
  | null
  | undefined;

export type NotifyStreamEventOptions = {
  suppressConnectedToast?: boolean;
  suppressPresenceToast?: boolean;
  suppressMatchUpdatedToast?: boolean;
  channelLabel?: string;
};

// Read the active UI language at delivery time, including after a language switch.
const streamText = (key: string, params?: Record<string, string | number>) =>
  translate(typeof document !== 'undefined' && document.documentElement.lang === 'nl' ? 'nl' : 'en', key, params);

const toastIds = {
  connected: 'sse-connected',
  matchUpdated: 'sse-match-updated',
  matchDeleted: 'sse-match-deleted',
  liveEventCreated: 'sse-live-event-created',
  liveEventUpdated: 'sse-live-event-updated',
  liveEventDeleted: 'sse-live-event-deleted',
  liveEventsReset: 'sse-live-events-reset',
  presence: 'sse-presence-updated',
} as const;

const desktopOnlyEventToast = {
  className: 'max-[899px]:hidden',
} as const;

const getPresenceToastId = (payload: PresenceUpdatedPayload): ToastId =>
  `${toastIds.presence}:${payload.channelKey || payload.matchId || payload.matchKey || 'global'}`;

const getChannelLabel = (payload: PresenceUpdatedPayload, fallback?: string): string =>
  fallback
  || payload.channelKey
  || payload.matchKey
  || payload.matchId
  || streamText('stream_channel');

const getLiveUserCount = (payload: PresenceUpdatedPayload): number | null =>
  typeof payload.liveUsers === 'number' && Number.isFinite(payload.liveUsers)
    ? payload.liveUsers
    : null;

export const notifyStreamEvent = (
  eventName: MatchStreamEventName,
  payload?: MatchStreamPayload,
  options: NotifyStreamEventOptions = {}
) => {
  switch (eventName) {
    case 'heartbeat':
    case 'server.stopping':
      return;
    case 'connected': {
      if (options.suppressConnectedToast) return;
      toast.info(streamText('stream_connected'), {
        toastId: toastIds.connected,
      });
      return;
    }

    case 'match.updated': {
      if (options.suppressMatchUpdatedToast) return;
      toast.info(streamText('stream_match_updated'), {
        toastId: toastIds.matchUpdated,
      });
      return;
    }

    case 'match.deleted': {
      toast.warning(streamText('stream_match_deleted'), {
        toastId: toastIds.matchDeleted,
      });
      return;
    }

    case 'live_event.created': {
      toast.success(streamText('stream_event_created'), {
        toastId: toastIds.liveEventCreated,
        ...desktopOnlyEventToast,
      });
      return;
    }

    case 'live_event.updated': {
      toast.info(streamText('stream_event_updated'), {
        toastId: toastIds.liveEventUpdated,
        ...desktopOnlyEventToast,
      });
      return;
    }

    case 'live_event.deleted': {
      toast.warning(streamText('stream_event_deleted'), {
        toastId: toastIds.liveEventDeleted,
        ...desktopOnlyEventToast,
      });
      return;
    }

    case 'live_events.reset': {
      toast.warning(streamText('stream_events_reset'), {
        toastId: toastIds.liveEventsReset,
        ...desktopOnlyEventToast,
      });
      return;
    }

    case 'roadmap.updated':
      return;

    case 'presence.updated': {
      if (options.suppressPresenceToast) return;
      const presencePayload = (payload && typeof payload === 'object'
        ? payload
        : {}) as PresenceUpdatedPayload;
      const liveUsers = getLiveUserCount(presencePayload);
      const channelLabel = getChannelLabel(presencePayload, options.channelLabel);
      const message = liveUsers === null
        ? streamText('stream_presence_updated', { channel: channelLabel })
        : streamText(liveUsers === 1 ? 'stream_presence_one' : 'stream_presence_many', { count: liveUsers, channel: channelLabel });

      toast.info(message, {
        toastId: getPresenceToastId(presencePayload),
      });
      return;
    }

    default: {
      const exhaustiveCheck: never = eventName;
      return exhaustiveCheck;
    }
  }
};
